import * as preact from "preact";

import * as appShell from "appShell";

export interface PreactPageProps {
  /** Whether it's the page shown: false while a page kept alive is hidden */
  active?: boolean;
  appRouter: any;
  options?: any;
  renderedCallback?: (renderedOptions?: any) => void;
  /** For a page kept alive: how many times its tab's been reselected */
  reselections?: number;
}

type ShownProps = Pick<PreactPageProps, "active" | "reselections">;

/**
 * Whether a page kept alive has just been come back to, or its tab
 * reselected, to fetch what may have changed meanwhile (see AppRootView.tsx)
 */
export const shownAgain = (props: ShownProps, previousProps: ShownProps) =>
  Boolean(
    props.active &&
      (!previousProps.active ||
        props.reselections !== previousProps.reselections)
  );

export interface PreactPageOptions {
  name: string;
  showHeader: boolean;
  Child: preact.ComponentType<PreactPageProps>;
  deferRenderedCallback?: boolean;
  preserveOnRerender?: boolean;
  preserveKey?: (options?: any) => unknown;
  /**
   * For pages to come back to as left (the tabs' pages), the slot it's kept
   * in, one page to a slot: see AppRootView.tsx for the rules
   */
  keepAlive?: (options?: any) => string;
}

export default class PreactPage {
  name: string;
  showHeader: boolean;
  Child: preact.ComponentType<PreactPageProps>;
  deferRenderedCallback: boolean;
  preserveOnRerender: boolean;
  preserveKey?: (options?: any) => unknown;
  keepAlive?: (options?: any) => string;
  preserveKeyValue: unknown;
  containerElement: HTMLElement | undefined;

  constructor(options: PreactPageOptions) {
    this.name = options.name;
    this.showHeader = options.showHeader;
    this.Child = options.Child;
    this.deferRenderedCallback = Boolean(options.deferRenderedCallback);
    this.preserveOnRerender = Boolean(options.preserveOnRerender);
    this.preserveKey = options.preserveKey;
    this.keepAlive = options.keepAlive;
  }

  async render(element, renderedCallback, appRouter, options) {
    await appShell.renderPage(
      element,
      this,
      renderedCallback,
      appRouter,
      options
    );
  }

  /** Left for another page, which the app root shows in its place */
  close() {
    this.containerElement = undefined;
    this.preserveKeyValue = undefined;
  }
}
